import { Injectable, inject, type Signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { map, tap, catchError, of, Observable } from "rxjs";
import type { AuthUser, LoginRequest } from "../interfaces/auth.interface";
import { AuthStateService } from "../state/auth-state.service";
import { environment } from "../../environments/environment";

interface LoginResponseDto {
  user: AuthUser;
}

/**
 * Authentication service using cookie-based auth.
 * Tokens are stored in HttpOnly cookies by the backend.
 * This service manages user session state and API calls.
 */
@Injectable({ providedIn: "root" })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly state = inject(AuthStateService);
  private readonly baseUrl = `${environment.apiUrl}/api/v1/auth`;

  /**
   * Login with username and password.
   * Backend sets HttpOnly cookies on success.
   */
  login(request: LoginRequest): Observable<AuthUser> {
    return this.http
      .post<LoginResponseDto>(`${this.baseUrl}/login`, request, {
        withCredentials: true,
      })
      .pipe(
        tap((response) => this.state.setUser(response.user)),
        map((response) => response.user),
      );
  }

  /**
   * Logout and clear session.
   * Backend clears cookies on logout.
   */
  logout(): Observable<void> {
    return this.http
      .post<void>(`${this.baseUrl}/logout`, {}, { withCredentials: true })
      .pipe(
        tap(() => {
          this.state.clearUser();
          void this.router.navigate(["/login"]);
        }),
        catchError(() => {
          // Clear local state even if backend call fails
          this.state.clearUser();
          void this.router.navigate(["/login"]);
          return of(undefined);
        }),
      );
  }

  /**
   * Check if the user has a valid session.
   * Called on app initialization to restore session from cookies.
   */
  checkSession(): Observable<AuthUser | null> {
    return this.http
      .get<LoginResponseDto>(`${this.baseUrl}/me`, { withCredentials: true })
      .pipe(
        map((response) => response.user),
        tap((user) => this.state.setUser(user)),
        catchError(() => {
          this.state.clearUser();
          return of(null);
        }),
      );
  }

  /**
   * Handle unauthorized response (401).
   * Clears local user state.
   */
  handleUnauthorized(): void {
    this.state.clearUser();
  }

  /**
   * Clear local session state without backend call.
   */
  clearSession(): void {
    this.state.clearUser();
  }

  /**
   * Get the current user as a signal.
   */
  user(): Signal<AuthUser | undefined> {
    return this.state.user;
  }

  /**
   * Get authentication status as a signal.
   */
  isAuthenticated(): Signal<boolean> {
    return this.state.isAuthenticated;
  }
}
